import React, { useState } from 'react';
import { Alert, Button, Input, Space, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import type { MissingRequirement } from '../../types/requirements';

const { Text } = Typography;

/**
 * G2 — the Send-dialog checkpoint (spec D3).
 *
 * This never refuses a send. It requires a DECISION: staff either know the
 * information is coming, or they waive the field by name with a reason recorded
 * against their user. The failure it exists to prevent is silent — an estimate
 * goes out, the customer opens it, and the approval gate refuses them for a field
 * nobody at the shop knew was blank.
 *
 * ── The list is the SERVER's ───────────────────────────────────────────────
 *
 * There is no field list in this file, and no rule about which fields matter.
 * `missing` is derived from `GET /api/admin/completeness/resolved`, labels
 * included, so what staff read here is the same rule the gate will apply. A
 * second copy of the policy in the client would drift, and the drift would only
 * ever be discovered by a customer.
 */

export type { MissingRequirement } from '../../types/requirements';

export interface MissingRequirementsChecklistProps {
  estimateId: number;
  /**
   * Whose record is short. Carried so a future standing (per-customer) waiver can
   * be offered from the same row; the estimate-scoped waiver below does not need
   * it, and the server derives the customer from the estimate regardless. Optional
   * because a caller that has not loaded the customer must pass nothing rather than
   * invent a sentinel id.
   */
  customerId?: number | null;
  missing: MissingRequirement[];
  /** Called with the field name once the server has accepted a waiver. */
  onWaived: (field: string) => void;
}

/** Per-row waiver state. Only one row is ever open at a time in practice. */
interface RowState {
  open: boolean;
  reason: string;
  error: string;
  saving: boolean;
  waived: boolean;
}

const EMPTY_ROW: RowState = { open: false, reason: '', error: '', saving: false, waived: false };

export const MissingRequirementsChecklist: React.FC<MissingRequirementsChecklistProps> = ({
  estimateId,
  // `customerId` is part of the contract (the standing per-customer waiver is the
  // follow-up) but the estimate-scoped waiver below does not need it: the server
  // derives the customer from the estimate, inside the shop scope.
  missing,
  onWaived,
}) => {
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const rowOf = (field: string): RowState => rows[field] ?? EMPTY_ROW;
  const patchRow = (field: string, patch: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [field]: { ...(prev[field] ?? EMPTY_ROW), ...patch } }));

  // A checkpoint with nothing to decide is noise. Render nothing at all — the
  // Send button is the only thing left on the dialog.
  if (!missing || missing.length === 0) return null;

  const submitWaiver = async (field: string) => {
    const row = rowOf(field);
    const reason = row.reason.trim();
    if (!reason) {
      // Deliberately not posted. A waiver with no reason cannot be reviewed
      // later, and the server refuses it anyway — say so here instead of
      // spending a round trip to be told.
      patchRow(field, { error: 'A reason is required before this can be waived.' });
      return;
    }

    patchRow(field, { saving: true, error: '' });
    try {
      // `optional` — not deleted from the policy. The shop still wants the field;
      // this one estimate stops blocking on it.
      await api.post(`/completeness/waivers/estimate/${estimateId}`, {
        field,
        level: 'optional',
        reason,
      });
      patchRow(field, { saving: false, open: false, waived: true });
      onWaived(field);
    } catch (err: unknown) {
      // A `mandatory` field is refused by the server for everyone, owner
      // included. Show the server's own sentence: swallowing it would leave
      // staff clicking a button that appears to do nothing.
      const data = (err as { response?: { data?: { message?: string; error?: string } } })?.response
        ?.data;
      patchRow(field, {
        saving: false,
        error: data?.message || data?.error || 'Could not record that waiver. Please try again.',
      });
    }
  };

  return (
    <div data-testid="missing-requirements-checklist" style={{ marginTop: 16 }}>
      <Alert
        type="warning"
        showIcon
        message="Some details are still missing"
        description={
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
              You can still send this — the customer will be asked for anything still missing
              before they approve. Waive a field if it genuinely does not apply.
            </Text>

            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {missing.map((m) => {
                const row = rowOf(m.field);
                return (
                  <div key={m.field} data-testid={`missing-row-${m.field}`}>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        {m.label}
                      </Text>
                      {row.waived ? (
                        <Text
                          type="success"
                          style={{ fontSize: 13 }}
                          data-testid={`waived-${m.field}`}
                        >
                          <CheckCircleOutlined /> Waived
                        </Text>
                      ) : (
                        !row.open && (
                          <Button
                            size="small"
                            data-testid={`waive-${m.field}`}
                            onClick={() => patchRow(m.field, { open: true, error: '' })}
                            // 44px tall: this dialog is used one-handed in a
                            // driveway, not at a desk.
                            style={{ minHeight: 44, paddingInline: 12 }}
                          >
                            Not needed for this one
                          </Button>
                        )
                      )}
                    </div>

                    {row.open && (
                      <div style={{ marginTop: 6 }}>
                        <Text style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                          Reason (required)
                        </Text>
                        <Input
                          data-testid={`waive-reason-${m.field}`}
                          aria-label={`Reason for waiving ${m.label}`}
                          value={row.reason}
                          onChange={(e) => patchRow(m.field, { reason: e.target.value, error: '' })}
                          placeholder="e.g. classic car, no VIN plate"
                          // 16px stops mobile Safari zooming the viewport on focus.
                          style={{ fontSize: 16 }}
                        />
                        <Space style={{ marginTop: 6 }}>
                          <Button
                            size="small"
                            type="primary"
                            loading={row.saving}
                            data-testid={`waive-confirm-${m.field}`}
                            onClick={() => submitWaiver(m.field)}
                            style={{ minHeight: 44, paddingInline: 12 }}
                          >
                            Waive this field
                          </Button>
                          <Button
                            size="small"
                            data-testid={`waive-cancel-${m.field}`}
                            onClick={() => patchRow(m.field, { open: false, error: '' })}
                            style={{ minHeight: 44, paddingInline: 12 }}
                          >
                            Keep asking
                          </Button>
                        </Space>
                        {row.error && (
                          <Text
                            type="danger"
                            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
                            data-testid={`waive-error-${m.field}`}
                          >
                            {row.error}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Space>
          </div>
        }
      />
    </div>
  );
};

export default MissingRequirementsChecklist;
